import React, {ReactElement, useCallback, useEffect, useState} from 'react';
import {Loader} from '@googlemaps/js-api-loader';

import {GoogleMapsLibraryStatus, WrapperProps} from './IGoogleMapsWrapper';

/**
 * A component to wrap the loading of the Google Maps JavaScript API.
 *
 * @param props
 */
export const GoogleMapsWrapper = ({children, render, callback, reload, ...options}: WrapperProps): ReactElement => {
	const [status, setStatus] = useState(GoogleMapsLibraryStatus.LOADING);

	const deleteScripts = useCallback(() => {
		/** Delete the script and unset the library on window */
		delete window.google;
		Loader.instance.deleteScript();
		Loader.instance.reset();
		document
			.querySelectorAll('script[src^="https://maps.googleapis.com/maps-api-v3/api/js"]')
			.forEach((script) => script.remove());
		/** Force new instance otherwise doesn't accept the new options */
		Loader.instance = undefined;
	}, []);

	const setStatusAndExecuteCallback = useCallback((status: GoogleMapsLibraryStatus) => {
		if (callback) {
			callback(status, Loader.instance);
		}

		setStatus(status);
	}, []);

	const loadScript = useCallback((loader) => {
		setStatusAndExecuteCallback(GoogleMapsLibraryStatus.LOADING);

		Promise.all(options.libraries.map((library) => loader.importLibrary(library))).then(() => {
			setStatusAndExecuteCallback(GoogleMapsLibraryStatus.SUCCESS);
		});
	}, [options]);

	useEffect(() => {
		try {
			loadScript(new Loader(options));
		} catch (e) {
			if (e.message.startsWith('Loader must not be called again with different options')) {
				try {
					setStatusAndExecuteCallback(GoogleMapsLibraryStatus.RESETTING);
					deleteScripts();
					loadScript(new Loader(options));
				} catch (e) {
					setStatusAndExecuteCallback(GoogleMapsLibraryStatus.FAILURE);
				}
			}
		}
	}, [options.language]);

	if (status === GoogleMapsLibraryStatus.SUCCESS && children) {
		return <>{children}</>;
	}

	if (render) {
		return render(status);
	}

	return <></>;
};
